<template>
  <div>
    <div class="my-2">
      <h2 class="font-weight-bolder">{{ $t('Find Shipment') }}</h2>
      <p class="text-muted">
        {{ $t('Smart Shipment Subtitle') }}
      </p>
    </div>

    <b-card no-body v-if="!hasQueried">
      <b-card-body>
        <div class="d-flex align-items-center justify-content-between mb-2">
          <div class="d-flex align-items-center flex-wrap">
            <label class="font-weight-bold mb-0 mr-1">{{ t('Query Type') }}</label>
            <b-button-group class="mr-1">
              <b-button
                :variant="queryType === 'tracking_code' ? 'primary' : 'outline-primary'"
                @click="setQueryType('tracking_code')"
                size="sm"
              >
                {{ t('Tracking Code') }}
              </b-button>
              <b-button
                :variant="queryType === 'reference' ? 'primary' : 'outline-primary'"
                @click="setQueryType('reference')"
                size="sm"
              >
                {{ t('Reference No') }}
              </b-button>
            </b-button-group>
          </div>

          <b-form-checkbox v-model="quickPrint" switch class="custom-control-primary">
            <span class="font-weight-bold">{{ $t('Hızlı Etiket Yazdır') }}</span>
          </b-form-checkbox>
        </div>

        <b-form-textarea
          id="barcodeInput"
          ref="barcodeInput"
          v-model="inputText"
          rows="10"
          :placeholder="placeholderText"
          @keydown.native="handleKeyDown"
          @paste.native="handlePaste"
          @blur.native="formatInputToLines"
          class="mb-1"
        ></b-form-textarea>

        <div class="d-flex justify-content-between align-items-center mb-2">
          <small class="text-muted">{{ t('One code per line. Empty lines are ignored.') }}</small>
          <small class="font-weight-bold" :class="{ 'text-danger': validLines.length > 50 }">
            {{ validLines.length }} / 50 {{ t('codes') }}
          </small>
        </div>

        <b-button
          variant="primary"
          @click="startQuery"
          :disabled="validLines.length === 0 || validLines.length > 50 || isQuerying"
        >
          <b-spinner small v-if="isQuerying" class="mr-50"></b-spinner>
          {{ quickPrint ? $t('Hızlı Etiket Yazdır') : t('Query') }}
        </b-button>
      </b-card-body>
    </b-card>

    <div v-else>
      <b-card no-body class="mb-2">
        <b-card-body>
          <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <label class="font-weight-bold mb-0 mr-1">{{ t('Query Type') }}</label>
              <b-button-group>
                <b-button
                  :variant="queryType === 'tracking_code' ? 'primary' : 'outline-primary'"
                  size="sm"
                  disabled
                >
                  {{ t('Tracking Code') }}
                </b-button>
                <b-button
                  :variant="queryType === 'reference' ? 'primary' : 'outline-primary'"
                  size="sm"
                  disabled
                >
                  {{ t('Reference No') }}
                </b-button>
              </b-button-group>
            </div>
            <b-button
              variant="primary"
              size="sm"
              @click="resetQuery"
              class="d-flex align-items-center"
            >
              <feather-icon icon="RefreshCwIcon" size="14" class="mr-50" />
              <span>{{ $t('New Query') }}</span>
            </b-button>
          </div>
        </b-card-body>
      </b-card>

      <b-card no-body>
        <b-card-body>
          <div class="d-flex justify-content-between align-items-center mb-1">
            <b-tabs pills v-model="activeTab" nav-class="mb-0">
              <b-tab>
                <template #title>
                  <span class="mr-50">{{ t('All') }}</span>
                  <b-badge
                    :variant="activeTab === 0 ? 'light' : 'light-primary'"
                    :class="['rounded-pill tab-badge', { 'text-primary': activeTab === 0 }]"
                  >
                    {{ results.length }}
                  </b-badge>
                </template>
              </b-tab>
              <b-tab>
                <template #title>
                  <span class="mr-50">{{ t('Success') }}</span>
                  <b-badge
                    :variant="activeTab === 1 ? 'light' : 'light-success'"
                    :class="['rounded-pill tab-badge', { 'text-primary': activeTab === 1 }]"
                  >
                    {{ successCount }}
                  </b-badge>
                </template>
              </b-tab>
              <b-tab>
                <template #title>
                  <span class="mr-50">{{ t('Failed') }}</span>
                  <b-badge
                    :variant="activeTab === 2 ? 'light' : 'light-danger'"
                    :class="['rounded-pill tab-badge', { 'text-primary': activeTab === 2 }]"
                  >
                    {{ errorCount }}
                  </b-badge>
                </template>
              </b-tab>
            </b-tabs>

            <b-button
              variant="success"
              size="sm"
              @click="$refs['bulk-download-modal'].show()"
              v-if="results.length > 0"
            >
              {{ $t('Download (.xls)') }}
              <span class="badge bg-white text-success ml-50"
                >{{ filteredResults.length }} {{ $t('records') }}</span
              >
            </b-button>
          </div>

          <vue-good-table
            :columns="columns"
            :rows="filteredResults"
            :pagination-options="{ enabled: false }"
            :sort-options="{ enabled: false }"
            styleClass="vgt-table striped"
          >
            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field === 'code'">
                <span class="font-weight-bold">{{ props.row.code }}</span>
              </span>
              <span v-else-if="props.column.field === 'status'">
                <b-spinner small variant="primary" v-if="props.row.loading"></b-spinner>
                <div
                  v-else-if="props.row.success"
                  class="d-flex align-items-center text-success font-small-3 font-weight-bold"
                >
                  <span class="status-dot bg-success mr-50"></span>
                  <span>{{ props.row.statusMessage }}</span>
                </div>
                <div
                  v-else-if="props.row.error"
                  class="d-flex align-items-center text-danger font-small-3 font-weight-bold"
                >
                  <span class="status-dot bg-danger mr-50"></span>
                  <span>{{ props.row.statusMessage }}</span>
                </div>
                <span v-else>-</span>
              </span>
              <span v-else-if="props.column.field === 'provider'">
                <div
                  v-if="props.row.data && props.row.data.provider"
                  class="d-flex align-items-center"
                >
                  <img
                    :src="getProviderLogo(props.row.data.provider)"
                    :alt="props.row.data.provider.name + ' Logo'"
                    class="provider-logo"
                  />
                  <span class="ml-1">{{ props.row.data.provider.name }}</span>
                </div>
                <span v-else>-</span>
              </span>
              <span v-else-if="props.column.field === 'detail'">
                <b-link
                  v-if="props.row.success && props.row.data"
                  class="font-weight-bold text-primary"
                  @click="openDetail(props.row.code)"
                >
                  {{ t('View Detail') }}
                </b-link>
                <span v-else>-</span>
              </span>
              <span v-else-if="props.column.field === 'edit'">
                <b-button
                  v-if="props.row.success && props.row.data && props.row.data.id"
                  variant="outline-primary"
                  size="sm"
                  class="py-50 px-1 d-inline-flex align-items-center"
                  @click="editConsignment(props.row.data.id)"
                >
                  <feather-icon icon="Edit3Icon" size="14" class="mr-50" />
                  <span>{{ $t('Edit') }}</span>
                </b-button>
                <span v-else>-</span>
              </span>
              <span v-else-if="props.column.field === 'label'">
                <b-button
                  v-if="props.row.success && props.row.data"
                  variant="primary"
                  size="sm"
                  class="py-50 px-1"
                  @click="openDownloadModal(props.row)"
                >
                  {{ t('Download') }}
                </b-button>
                <span v-else>-</span>
              </span>
              <span v-else-if="props.column.field === 'consignor'">
                <span v-if="props.row.data && props.row.data.consignor">{{
                  props.row.data.consignor.name || '-'
                }}</span>
                <span v-else>-</span>
              </span>
              <span v-else-if="props.column.field === 'consignee'">
                <span v-if="props.row.data && props.row.data.consignee">{{
                  props.row.data.consignee.name || '-'
                }}</span>
                <span v-else>-</span>
              </span>
              <span v-else-if="props.column.field === 'content'">
                <span v-if="props.row.data && props.row.data.packages">
                  {{ props.row.data.packages.length }} {{ t('Package') }} /
                  {{ props.row.data.packages.reduce((sum, pkg) => sum + (pkg.weight || 0), 0) }} kg
                </span>
                <span v-else>-</span>
              </span>
              <span v-else-if="props.column.field === 'lastMovement'">
                <span
                  v-if="
                    props.row.data && props.row.data.details && props.row.data.details.length > 0
                  "
                >
                  <div class="text-nowrap">
                    {{
                      formatDate(
                        props.row.data.details[props.row.data.details.length - 1].status_datetime
                      )
                    }}
                  </div>
                  <div class="text-muted small">
                    {{
                      props.row.data.details[props.row.data.details.length - 1].message ||
                      props.row.data.details[props.row.data.details.length - 1].status_description
                    }}
                  </div>
                </span>
                <span v-else-if="props.row.data && props.row.data.status_message">
                  <div class="text-nowrap">
                    {{ formatDate(props.row.data.updated_at || props.row.data.created_at) }}
                  </div>
                  <div class="text-muted small">
                    {{ props.row.data.status_message }}
                  </div>
                </span>
                <span v-else>-</span>
              </span>
              <span v-else>
                {{ props.formattedRow[props.column.field] }}
              </span>
            </template>
            <template slot="emptystate">
              <div class="text-center p-2">{{ t('No records match this filter') }}</div>
            </template>
          </vue-good-table>
        </b-card-body>
      </b-card>
    </div>

    <b-modal
      id="redirect-modal"
      ref="redirect-modal"
      :title="$t('Consignment Detail')"
      centered
      @ok="confirmRedirect"
      :ok-title="$t('Open link')"
      :cancel-title="$t('Cancel')"
      ok-variant="primary"
    >
      <p class="mb-1">
        {{ $t('You are about to visit the shipment details page. Do you want to proceed?') }}
      </p>
    </b-modal>

    <b-modal
      id="download-modal"
      ref="download-modal"
      :title="$t('Download Label')"
      centered
      @ok="confirmDownload"
      :ok-title="$t('Download')"
      :cancel-title="$t('Cancel')"
      ok-variant="primary"
    >
      <p class="mb-1">
        {{
          $t(
            'You are about to download the label document for the selected consignment. Do you want to proceed?'
          )
        }}
      </p>
    </b-modal>

    <b-modal
      id="bulk-download-modal"
      ref="bulk-download-modal"
      :title="$t('Bulk Export')"
      centered
      @ok="confirmBulkDownload"
      :ok-title="$t('Download')"
      :cancel-title="$t('Cancel')"
      ok-variant="primary"
    >
      <p class="mb-1">
        {{
          $t(
            'You are about to export the filtered records to an Excel file. Do you want to proceed?'
          )
        }}
      </p>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BButtonGroup,
  BSpinner,
  BBadge,
  BTabs,
  BTab,
  BLink,
  BModal
} from 'bootstrap-vue';
import { VueGoodTable } from 'vue-good-table';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import useService from '@/auth/service/useService';
import { helper } from '@core/mixins/helper';
import { getProviderLogo } from '@/libs/providerHelper';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

export default {
  name: 'BulkFindShipment',
  components: {
    BCard,
    BCardBody,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BButtonGroup,
    BSpinner,
    BBadge,
    BTabs,
    BTab,
    BLink,
    BModal,
    VueGoodTable
  },
  mixins: [helper],
  data() {
    return {
      queryType: 'tracking_code',
      quickPrint: localStorage.getItem('quick_print_enabled') === 'true',
      inputText: '',
      hasQueried: false,
      isQuerying: false,
      results: [],
      activeTab: 0,
      redirectUrl: '',
      selectedRowForDownload: null
    };
  },
  mounted() {
    this.focusInput();
  },
  watch: {
    quickPrint(val) {
      localStorage.setItem('quick_print_enabled', val);
      if (val) {
        this.focusInput();
      }
    }
  },
  computed: {
    validLines() {
      const rawLines = this.inputText
        .split(/[,\s;\t\n\r]+/)
        .map(code => code.trim())
        .filter(code => code.length > 0);

      return Array.from(new Set(rawLines)).slice(0, 50);
    },
    successCount() {
      return this.results.filter(r => r.success).length;
    },
    errorCount() {
      return this.results.filter(r => r.error).length;
    },
    placeholderText() {
      if (this.queryType === 'tracking_code') {
        return this.$t('Tracking Placeholder');
      }
      return this.$t('Reference Placeholder');
    },
    filteredResults() {
      if (this.activeTab === 1) return this.results.filter(r => r.success);
      if (this.activeTab === 2) return this.results.filter(r => r.error);
      return this.results;
    },
    columns() {
      return [
        {
          label:
            this.queryType === 'tracking_code' ? this.$t('Tracking No') : this.$t('Reference No'),
          field: 'code',
          thClass: 'text-nowrap',
          tdClass: 'text-nowrap'
        },
        {
          label: this.$t('Status'),
          field: 'status'
        },
        {
          label: this.$t('Consignor'),
          field: 'consignor'
        },
        {
          label: this.$t('Consignee'),
          field: 'consignee'
        },
        {
          label: this.$t('Content'),
          field: 'content'
        },
        {
          label: this.$t('Provider'),
          field: 'provider'
        },
        {
          label: this.$t('Last Movement'),
          field: 'lastMovement'
        },
        {
          label: this.$t('Detail'),
          field: 'detail'
        },
        {
          label: this.$t('Edit'),
          field: 'edit'
        },
        {
          label: this.$t('Label'),
          field: 'label'
        }
      ];
    }
  },
  methods: {
    focusInput() {
      this.$nextTick(() => {
        const el = document.getElementById('barcodeInput');
        if (el) el.focus();
      });
    },
    getProviderLogo,
    formatDate(val) {
      if (!val) return '-';
      return dayjs(val).format('DD/MM/YYYY HH:mm');
    },
    setQueryType(type) {
      if (this.queryType !== type) {
        this.queryType = type;
        this.resetQuery();
      }
    },
    formatInputToLines() {
      const lines = this.validLines;
      if (lines.length > 0) {
        this.inputText = lines.join('\n');
      }
    },
    handlePaste() {
      this.$nextTick(() => {
        this.formatInputToLines();
      });
    },
    async handleKeyDown(e) {
      if (e.key === 'Enter') {
        if (this.quickPrint) {
          e.preventDefault();
          const codes = [...this.validLines];
          if (codes.length === 0) return;
          this.inputText = '';
          for (const code of codes) {
            await this.fetchAndPrintLabel(code);
          }
        } else if (!e.shiftKey && this.validLines.length > 0) {
          e.preventDefault();
          this.formatInputToLines();
          this.startQuery();
        }
      }
    },
    resetQuery() {
      this.inputText = '';
      this.results = [];
      this.hasQueried = false;
      this.isQuerying = false;
      this.activeTab = 0;
    },
    async startQuery() {
      const lines = this.validLines;
      if (lines.length === 0) return;

      if (this.quickPrint) {
        const codes = [...lines];
        this.inputText = '';
        for (const code of codes) {
          await this.fetchAndPrintLabel(code);
        }
        return;
      }

      this.formatInputToLines();

      if (lines.length === 1) {
        const query = { code: lines[0] };
        if (this.queryType !== 'tracking_code') query.type = this.queryType;

        this.$router.push({
          name: 'find_shipment_detail',
          query
        });
        return;
      }

      this.hasQueried = true;
      this.isQuerying = true;
      this.activeTab = 0;

      const successText = this.$t('Success');
      const notFoundText = this.$t('Kayıt Bulunamadı') || 'Kayıt Bulunamadı';
      const errorText = this.$t('Failed') || 'Hata Oluştu';
      const loadingText = 'Loading...';

      this.results = lines.map(code => ({
        code,
        loading: true,
        success: false,
        error: false,
        statusMessage: loadingText,
        data: null
      }));

      await Promise.allSettled(
        this.results.map(async item => {
          try {
            const response =
              this.queryType === 'tracking_code'
                ? await useService.getConsigmentsFindShipments(item.code)
                : await useService.getConsignmentByReferenceNo(item.code);
            if (response && response.status && response.data) {
              item.loading = false;
              item.success = true;
              item.data = JSON.parse(JSON.stringify(response.data));

              let statusMsg = response.message || successText;
              if (response.data.details && response.data.details.length > 0) {
                const lastDetail = response.data.details[response.data.details.length - 1];
                statusMsg = lastDetail.message || lastDetail.status_description || statusMsg;
              } else if (response.data.status_message) {
                statusMsg = response.data.status_message;
              }
              item.statusMessage = statusMsg;
            } else {
              let errMessage = response?.message || notFoundText;
              if (response?.errors && response.errors.length > 0) {
                errMessage = response.errors[0].message || errMessage;
              }
              throw new Error(errMessage);
            }
          } catch (err) {
            item.loading = false;
            item.success = false;
            item.error = true;

            let msg = errorText;

            if (err) {
              if (typeof err === 'string') {
                msg = err;
              } else if (Array.isArray(err) && err.length > 0 && err[0].message) {
                msg = err[0].message;
              } else if (err.message) {
                msg = err.message;
              }

              if (err.response && err.response.data) {
                const data = err.response.data;
                msg = data.message || msg;
                if (data.errors && data.errors.length > 0) {
                  msg = data.errors[0].message || msg;
                }
              }
            }

            if (
              msg.includes('Cannot read properties of null') ||
              msg.includes('Network Error') ||
              msg.includes('[object Object]')
            ) {
              msg = errorText;
            }

            item.statusMessage = this.$t(msg) || msg;
          }
        })
      );

      this.isQuerying = false;
    },
    openDetail(code) {
      if (!code) return;

      const query = { code };
      if (this.queryType !== 'tracking_code') query.type = this.queryType;

      const routeUrl = this.$router.resolve({
        name: 'find_shipment_detail',
        query
      });
      this.redirectUrl = routeUrl.href;
      this.$refs['redirect-modal'].show();
    },
    confirmRedirect() {
      if (this.redirectUrl) {
        window.open(this.redirectUrl, '_blank');
      }
      this.$refs['redirect-modal'].hide();
    },
    openDownloadModal(row) {
      if (!row.data || !row.data.id) return;
      this.selectedRowForDownload = row;
      this.$refs['download-modal'].show();
    },
    async confirmDownload() {
      const row = this.selectedRowForDownload;
      this.$refs['download-modal'].hide();
      this.selectedRowForDownload = null;
      if (!row || !row.data || !row.data.id) return;

      try {
        if (row.data.labels && row.data.labels.length > 0) {
          const preferredLabel = row.data.labels.find(
            l => l && (l.label_type === 0 || l.label_type === '0')
          );
          if (preferredLabel && preferredLabel.file_url) {
            const response = await useService.downloadLabelDocument(preferredLabel.file_url);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'labelFile.pdf');
            document.body.appendChild(link);
            link.click();
            return;
          }
        }

        const payload = await useService.startProcessManually(row.data.id);
        const payloadData = payload.data || {};
        const base64 = payloadData.base64label || payload.base64label;
        const contentType =
          payloadData.contentType || payloadData.content_type || payload.contentType || '';

        if (base64) {
          let fileFormat = 'pdf';
          if (contentType && contentType.startsWith('image/')) {
            fileFormat = contentType.split('/')[1];
          }

          const fileContent =
            fileFormat === 'pdf'
              ? URL.createObjectURL(this.base64ToBlob(base64, 'application/pdf'))
              : `data:${contentType};base64,${base64}`;

          const fileName = `labelFile.${fileFormat}`;
          const downloadLink = document.createElement('a');
          downloadLink.href = fileContent;
          downloadLink.setAttribute('download', fileName);
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        } else {
          this.showErrorMessage(this.$t('An error occurred'));
        }
      } catch (err) {
        console.error(err);
        this.showErrorMessage(this.$t('An error occurred'));
      }
    },
    confirmBulkDownload() {
      this.exportToExcel();
      this.$refs['bulk-download-modal'].hide();
    },
    exportToExcel() {
      const dataToExport = this.filteredResults.map(row => {
        let consignor = '-';
        let consignee = '-';
        let content = '-';
        let provider = '-';
        let lastMovement = '-';

        if (row.data) {
          consignor = row.data.consignor ? row.data.consignor.name : '-';
          consignee = row.data.consignee ? row.data.consignee.name : '-';
          if (row.data.packages) {
            content = `${row.data.packages.length} Package / ${row.data.packages.reduce(
              (sum, pkg) => sum + (pkg.weight || 0),
              0
            )} kg`;
          }
          provider = row.data.provider ? row.data.provider.name : '-';
          if (row.data.details && row.data.details.length > 0) {
            const lastDetail = row.data.details[row.data.details.length - 1];
            lastMovement = `${this.formatDate(lastDetail.status_datetime)} - ${
              lastDetail.message || lastDetail.status_description
            }`;
          } else if (row.data.status_message) {
            lastMovement = `${this.formatDate(row.data.updated_at || row.data.created_at)} - ${
              row.data.status_message
            }`;
          }
        }

        return {
          [this.queryType === 'barcode' ? 'Takip No' : 'Referans No']: row.code,
          Durum: row.statusMessage,
          Gönderen: consignor,
          Alıcı: consignee,
          İçerik: content,
          'Kargo Firması': provider,
          'Son Hareket': lastMovement
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Toplu Sorgulama');
      XLSX.writeFile(workbook, `Toplu_Sevkiyat_Sorgulama_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
    },
    async fetchAndPrintLabel(barcode) {
      if (!barcode) {
        this.showErrorMessage(this.$t('Barkod giriniz'));
        return;
      }

      this.isQuerying = true;
      try {
        const response =
          this.queryType === 'tracking_code'
            ? await useService.getConsigmentsFindShipments(barcode)
            : await useService.getConsignmentByReferenceNo(barcode);

        if (!response || !response.status || !response.data) {
          const errMessage =
            response?.errors?.[0]?.message || response?.message || this.$t('Kayıt Bulunamadı');
          this.showErrorMessage(`${barcode}: ${this.$t(errMessage) || errMessage}`);
          return;
        }

        const consignment = response.data;
        let printed = false;

        const preferredLabel = consignment.labels?.find(
          l => l && (l.label_type === 0 || l.label_type === '0' || l.file_url)
        );
        if (preferredLabel?.file_url) {
          try {
            const pdfResponse = await useService.downloadLabelDocument(preferredLabel.file_url);
            if (pdfResponse?.data) {
              this.printBlob(new Blob([pdfResponse.data], { type: 'application/pdf' }));
              printed = true;
            }
          } catch (e) {
            console.warn('downloadLabelDocument failed:', e);
          }
        }

        if (!printed && consignment.id) {
          try {
            const payload = await useService.startProcessManually(consignment.id);
            const payloadData = payload.data || payload;
            const base64 = payloadData.base64label;
            if (base64) {
              const contentType =
                payloadData.contentType || payloadData.content_type || 'application/pdf';
              this.printBlob(this.base64ToBlob(base64, contentType));
              printed = true;
            }
          } catch (e) {
            console.warn('startProcessManually label fetch failed:', e);
          }
        }

        if (printed) {
          this.showSuccessMessage(`${barcode}: ${this.$t('Etiket yazdırılıyor...')}`);
        } else {
          this.showErrorMessage(`${barcode}: ${this.$t('Etiket bulunamadı')}`);
        }
      } catch (err) {
        console.error('Quick print error:', err);
        let msg = this.$t('Failed') || 'Hata Oluştu';
        if (err) {
          if (typeof err === 'string') msg = err;
          else if (err.message) msg = err.message;
          if (err.response && err.response.data) {
            msg = err.response.data.message || err.response.data.errors?.[0]?.message || msg;
          }
        }
        this.showErrorMessage(`${barcode}: ${this.$t(msg) || msg}`);
      } finally {
        this.isQuerying = false;
        this.focusInput();
      }
    },
    printBlob(blob) {
      const blobUrl = URL.createObjectURL(blob);
      const frame = document.createElement('iframe');
      frame.style.display = 'none';
      document.body.appendChild(frame);

      const removeFrame = () => {
        if (!document.body.contains(frame)) return;
        URL.revokeObjectURL(blobUrl);
        frame.remove();
      };

      const safetyTimeout = setTimeout(removeFrame, 60000);

      frame.onload = () => {
        setTimeout(() => {
          try {
            frame.contentWindow.addEventListener('afterprint', () => {
              clearTimeout(safetyTimeout);
              removeFrame();
            });
          } catch (e) {
            console.warn('afterprint listener error:', e);
          }
          // print() blocks until a dialog is dismissed, or returns immediately
          // for silent/kiosk printing (afterprint is not reliable there).
          frame.contentWindow.print();
          this.focusInput();
        }, 300);
      };
      frame.src = blobUrl;
    }
  },
  setup() {
    const { t } = useI18nUtils();
    return { t };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>

<style scoped>
.provider-logo {
  height: 24px;
  object-fit: contain;
}

.bulk-find-shipment .vgt-table {
  border: 1px solid #ebe9f1;
}

.tab-badge {
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  width: 22px !important;
  height: 22px !important;
  min-width: 22px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 !important;
  border-radius: 50% !important;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}
</style>
